import './OfficialLibraryBadge.scss';

import * as React from 'react';

import { translate } from '~l10n/client';
import { SmallButton } from '~shared/components/buttons';
import { IconLock } from '~shared/components/icons/standard';
import { Popover } from '~shared/components/Popover';
import { useSocrativeRouter } from '~teacher/context/RouterContext';
import { useIsWorkspaceReadOnly } from '~teacher/hook/workspaces/settings';

interface Props {
  workspaceId: string;
  /** Positioning for the wrapper (spacing/alignment differs per surface). */
  className?: string;
}

/**
 * "Official Library" badge shown on read-only (Official) libraries. Click opens
 * a popover explaining the lock with a shortcut to the library settings.
 */
export function OfficialLibraryBadge({ workspaceId, className }: Props) {
  const isReadOnly = useIsWorkspaceReadOnly(workspaceId);
  const router = useSocrativeRouter();

  if (!isReadOnly) {
    return null;
  }

  // Navigating to the settings tab unmounts the badge (it only renders on
  // Quizzes/Deleted/editor), which also closes this popover.
  const goToSettings = () => {
    router.navigate(`workspaces/${workspaceId}/settings`, {
      trigger: true,
      replace: false,
    });
  };

  return (
    <div className={className}>
      <Popover
        mode="click"
        direction="bottom-start"
        disableArrow
        content={
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4 text-base text-blue-900">
              <span className="font-500">{translate('Official Library')}</span>
              <span>
                {translate(
                  "This library's content is locked and cannot be edited. It's been marked as official to preserve the integrity of its quizzes."
                )}
              </span>
            </div>
            <SmallButton
              appearance="default"
              className="w-full"
              onClick={goToSettings}
            >
              {translate('Go to Settings')}
            </SmallButton>
          </div>
        }
      >
        <button
          type="button"
          className="inline-flex cursor-pointer items-center gap-2 rounded-2 border-1 border-solid border-grey-200 bg-white px-3 py-2 font-600 text-sm text-grey-900"
        >
          <IconLock
            size="micro"
            className="official-library-badge__icon text-grey-900"
          />
          {translate('Official Library')}
        </button>
      </Popover>
    </div>
  );
}
